// Path: ./src/app/pages/CreatePreferenceCuisinePage/index.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TranslationResultDto } from "@swagger/typescript-fetch-goflyer";

import { messages } from "../../../locales/messages";
import { adminApiService } from "../../../utils/api";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { useLoading } from "../../../contextApi/LoadingContext";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { useFlyerItemCategories } from "../../../hooks/useFlyerItemCategories";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { routes } from "../../../utils/constant/navigationRoutes";
import {
  CuisineFormState,
  CuisineRow,
  cuisineToFormState,
  emptyCuisineFormState,
} from "../../../utils/preference/cuisinePayload";
import {
  AutocompleteControl,
  Field,
  TextControl,
} from "../../components/FormKit/formControls";
import {
  ControlRow,
  FormActions,
  FormShell,
  PageShell,
  SectionBody,
  SectionBox,
  SectionHeader,
  SectionTitle,
  token,
} from "../../components/FormKit/styled";
import { useHandleSaveCuisine } from "./handleSaveCuisine";

type TranslatableField = "nameChinese" | "nameFrench";

const TARGET_LANG: Record<TranslatableField, string> = {
  nameChinese: "zh",
  nameFrench: "fr",
};

const TRANSLATABLE_FIELDS: TranslatableField[] = ["nameChinese", "nameFrench"];

const CreatePreferenceCuisinePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cuisineId } = useParams<{ cuisineId?: string }>();
  const authHeaders = useAuthHeaders();
  const { showLoadingScreen } = useLoading();
  const handleSaveCuisine = useHandleSaveCuisine();

  const [form, setForm] = useState<CuisineFormState>(emptyCuisineFormState());
  const [savedSlug, setSavedSlug] = useState<string>();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pendingFields, setPendingFields] = useState<TranslatableField[]>([]);
  const [isTranslatingAll, setIsTranslatingAll] = useState(false);

  const { flyerItemCategories, fetchFlyerItemCategories } =
    useFlyerItemCategories();

  const isEditing = Boolean(cuisineId);

  useEffect(() => {
    fetchFlyerItemCategories();
  }, []);

  useEffect(() => {
    if (!cuisineId) return;

    (async () => {
      showLoadingScreen(true);
      const fetched =
        await adminApiService.getOneBaseGfPreferenceCuisineAdminControllerGfPreferenceCuisine(
          cuisineId,
          undefined,
          ["categories"],
          undefined,
          authHeaders,
        );
      showLoadingScreen(false);

      if (!fetched) return;

      const row = fetched as CuisineRow;
      setForm(cuisineToFormState(row));
      setSavedSlug(row.slug);
    })();
  }, [cuisineId]);

  const setField = <K extends keyof CuisineFormState>(
    key: K,
    value: CuisineFormState[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const categoryOptions = useMemo(
    () =>
      (flyerItemCategories ?? []).map((category) => ({
        label: category.name ?? "",
        value: category.id ?? "",
      })),
    [flyerItemCategories],
  );

  const requestTranslation = async (
    text: string,
    targetLang: string,
  ): Promise<string | undefined> => {
    const response =
      await adminApiService.gfMiscellaneousAdminControllerTranslateTextByGoogle(
        { text, sourceLang: "en", targetLang },
        authHeaders,
      );

    return (response as TranslationResultDto | undefined)?.result?.trim();
  };

  const handleTranslate = async (field: TranslatableField) => {
    const source = form.name.trim();
    if (!source) {
      toastError(t(messages.preference_cuisine_name_required()));
      return;
    }

    setPendingFields([field]);
    const translated = await requestTranslation(source, TARGET_LANG[field]);
    setPendingFields([]);

    if (!translated) return;

    setField(field, translated);
    toastSuccess(t(messages.preference_translate_filled()));
  };

  const handleTranslateEmpty = async () => {
    const source = form.name.trim();
    if (!source) {
      toastError(t(messages.preference_cuisine_name_required()));
      return;
    }

    const targets = TRANSLATABLE_FIELDS.filter((field) => !form[field].trim());
    if (!targets.length) {
      toastError(t(messages.preference_translate_nothing_to_do()));
      return;
    }

    setIsTranslatingAll(true);
    setPendingFields(targets);
    const filled: Partial<Record<TranslatableField, string>> = {};
    for (const field of targets) {
      const translated = await requestTranslation(source, TARGET_LANG[field]);
      if (translated) filled[field] = translated;
    }
    setPendingFields([]);
    setIsTranslatingAll(false);

    if (!Object.keys(filled).length) return;

    setForm((prev) => ({ ...prev, ...filled }));
    toastSuccess(t(messages.preference_translate_filled()));
  };

  const handleDelete = async () => {
    if (!cuisineId) return;
    showLoadingScreen(true);
    const result =
      await adminApiService.deleteOneBaseGfPreferenceCuisineAdminControllerGfPreferenceCuisine(
        cuisineId,
        authHeaders,
      );
    showLoadingScreen(false);
    if (result === undefined) return;

    setDeleteOpen(false);
    toastSuccess(t(messages.Successfully_Deleted()));
    navigate(routes.preferenceCuisine.view);
  };

  const renderTranslateButton = (field: TranslatableField) => {
    const hasSource = Boolean(form.name.trim());
    const label = hasSource
      ? t(messages.preference_translate_from_english())
      : t(messages.preference_translate_no_source());

    return (
      <Tooltip title={label} placement="top" arrow>
        <span>
          <IconButton
            size="small"
            aria-label={label}
            onClick={() => handleTranslate(field)}
            disabled={!hasSource || Boolean(pendingFields.length)}
            data-testid={`cuisine-translate-${field}`}
          >
            {pendingFields.includes(field) ? (
              <CircularProgress size={16} />
            ) : (
              <TranslateIcon
                sx={{ fontSize: 18, color: token.color.textMuted }}
              />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <PageShell>
      <AccountCardWrapper
        title={`${isEditing ? t(messages.Edit()) : t(messages.Create())} ${t(
          messages.preference_cuisine(),
        )}`}
      >
        <FormShell data-testid="create-preference-cuisine-page">
          <SectionBox>
            <SectionHeader>
              <SectionTitle>
                {t(messages.preference_section_labels())}
              </SectionTitle>

              <Tooltip
                title={t(messages.preference_translate_all_tooltip())}
                placement="top"
                arrow
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      isTranslatingAll ? (
                        <CircularProgress size={14} />
                      ) : (
                        <TranslateIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    disabled={
                      !form.name.trim() || Boolean(pendingFields.length)
                    }
                    onClick={handleTranslateEmpty}
                    data-testid="cuisine-translate-all"
                    sx={{
                      textTransform: "none",
                      fontSize: 13,
                      borderColor: token.color.border,
                      color: token.color.text,
                      "&:hover": {
                        borderColor: token.color.accent,
                        backgroundColor: token.color.accentSoft,
                      },
                    }}
                  >
                    {t(messages.preference_translate_all())}
                  </Button>
                </span>
              </Tooltip>
            </SectionHeader>

            <SectionBody>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Field
                    id="cuisine-name"
                    label={t(messages.preference_cuisine_name())}
                    tooltip={t(messages.preference_tip_cuisine_name())}
                    required
                    help={t(messages.preference_cuisine_name_helper())}
                  >
                    <TextControl
                      id="cuisine-name"
                      value={form.name}
                      onChange={(event) => setField("name", event.target.value)}
                      inputProps={{ "data-testid": "cuisine-name-input" }}
                    />
                  </Field>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Field
                    id="cuisine-name-chinese"
                    label={t(messages.preference_cuisine_name_chinese())}
                    tooltip={t(messages.preference_tip_locale_label())}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TextControl
                        id="cuisine-name-chinese"
                        value={form.nameChinese}
                        onChange={(event) =>
                          setField("nameChinese", event.target.value)
                        }
                        inputProps={{
                          "data-testid": "cuisine-name-chinese-input",
                        }}
                      />
                      {renderTranslateButton("nameChinese")}
                    </Stack>
                  </Field>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Field
                    id="cuisine-name-french"
                    label={t(messages.preference_cuisine_name_french())}
                    tooltip={t(messages.preference_tip_locale_label())}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TextControl
                        id="cuisine-name-french"
                        value={form.nameFrench}
                        onChange={(event) =>
                          setField("nameFrench", event.target.value)
                        }
                        inputProps={{
                          "data-testid": "cuisine-name-french-input",
                        }}
                      />
                      {renderTranslateButton("nameFrench")}
                    </Stack>
                  </Field>
                </Grid>

                <Grid item xs={6} md={3}>
                  <Field
                    id="cuisine-index"
                    label={t(messages.preference_cuisine_index())}
                    tooltip={t(messages.preference_tip_cuisine_index())}
                    help={t(messages.preference_cuisine_index_helper())}
                  >
                    <TextControl
                      id="cuisine-index"
                      type="number"
                      value={form.index}
                      onChange={(event) =>
                        setField("index", event.target.value)
                      }
                      inputProps={{ "data-testid": "cuisine-index-input" }}
                    />
                  </Field>
                </Grid>
              </Grid>
            </SectionBody>
          </SectionBox>

          <SectionBox>
            <SectionHeader>
              <SectionTitle>
                {t(messages.preference_section_categories())}
              </SectionTitle>
            </SectionHeader>

            <SectionBody>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Field
                    id="cuisine-categories"
                    label={t(messages.preference_cuisine_categories())}
                    tooltip={t(messages.preference_tip_cuisine_categories())}
                    help={t(messages.preference_cuisine_categories_helper())}
                  >
                    <AutocompleteControl
                      id="cuisine-categories"
                      options={categoryOptions}
                      selected={form.categoryIds}
                      onSelectedChange={(values) =>
                        setField("categoryIds", values)
                      }
                      dataTestId="cuisine-categories-select"
                    />
                  </Field>
                </Grid>
              </Grid>
            </SectionBody>
          </SectionBox>

          <FormActions>
            <Stack direction="row" spacing={1.5} alignItems="center">
              {isEditing && (
                <PermissionComponentWrapper
                  requiredPermission={["delete"]}
                  requiredPermissionGroup={["tag"]}
                >
                  <Button
                    onClick={() => setDeleteOpen(true)}
                    sx={{
                      textTransform: "none",
                      color: token.color.danger,
                      "&:hover": { background: "#fdecec" },
                    }}
                    data-testid="cuisine-delete-button"
                  >
                    {t(messages.Delete())}
                  </Button>
                </PermissionComponentWrapper>
              )}
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <ControlRow>
                <Button
                  variant="text"
                  onClick={() => navigate(-1)}
                  sx={{ textTransform: "none", color: token.color.textMuted }}
                  data-testid="cuisine-cancel-button"
                >
                  {t(messages.Cancel())}
                </Button>
              </ControlRow>
              <PermissionComponentWrapper
                requiredPermission={[isEditing ? "edit" : "create"]}
                requiredPermissionGroup={["tag"]}
              >
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => handleSaveCuisine(form, cuisineId)}
                  sx={{
                    minWidth: 132,
                    height: token.controlHeight,
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: token.radius.sm,
                    backgroundColor: token.color.accent,
                    "&:hover": { backgroundColor: token.color.accentHover },
                  }}
                  data-testid="cuisine-save-button"
                >
                  {isEditing ? t(messages.Update()) : t(messages.Save())}
                </Button>
              </PermissionComponentWrapper>
            </Stack>
          </FormActions>
        </FormShell>
      </AccountCardWrapper>

      <MuiConfirmationModal
        open={deleteOpen}
        handleCancel={() => setDeleteOpen(false)}
        handleConfirm={handleDelete}
        title={t(messages.Delete_Confirmation())}
        description={`${t(messages.Are_you_sure_you_want_to_delete_These())}? ${t(
          messages.This_action_cannot_be_undone(),
        )}. ${t(messages.preference_delete_slug_warning(), {
          slug: savedSlug ?? "",
        })}`}
      />
    </PageShell>
  );
};

export default CreatePreferenceCuisinePage;
